import { Alert, Button, Drawer, FileInput, Flex, List, Text } from '@mantine/core';
import { IconFileDescription } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import DrawerLayout from '@/components/ui/DrawerLayout';
import TreeNode from '@/components/ui/TreeNode';
import { IconFolder } from '@/components/ui/icons/IconFolder';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { MAX_UPLOAD_FILES, MAX_UPLOAD_FILE_SIZE_MB } from '@/stores/local/sidebar/UploadDocumentStore/utils';

import SourceSelect from './SourceSelect';
import TreeSelect from './TreeSelect';
import UploadProgress from './UploadProgress';

import s from './UploadDocumentPopup.module.scss';

const ACCEPTED_FILE_TYPES = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/pdf';

const UploadDocumentPopup: React.FC = () => {
  const {
    popups: { uploadDocumentPopup },
    selectionStore,
  } = useSidebarDocumentsStore();

  const { uploadDocumentStore, documentSourceTypeSelectStore } = uploadDocumentPopup;

  const handleChange = React.useCallback(
    (value: File | File[] | null) => {
      if (!value) {
        uploadDocumentStore.reset();

        return;
      }

      const nextFiles = Array.isArray(value) ? value : [value];

      uploadDocumentStore.setFiles(nextFiles);
    },
    [uploadDocumentStore],
  );

  const handleAddDocument = React.useCallback(() => {
    uploadDocumentPopup.addDocument(selectionStore.singleFolderToServerFormat);
  }, [selectionStore.singleFolderToServerFormat, uploadDocumentPopup]);

  const handleClose = React.useCallback(() => {
    if (uploadDocumentStore.meta.isLoading) {
      return;
    }

    uploadDocumentPopup.close();
  }, [uploadDocumentPopup, uploadDocumentStore]);

  const folder = uploadDocumentPopup.entity;
  const { files, failedFiles, meta } = uploadDocumentStore;
  const addButtonLabel = files.length > 1 ? `Добавить (${files.length})` : 'Добавить';
  const showFailedFilesAlert = failedFiles.length > 0 && !meta.isLoading;
  const failedFilesAlertTitle = failedFiles.length === 1 ? 'Не удалось загрузить файл' : 'Не удалось загрузить файлы';

  return (
    <Drawer
      classNames={{ body: s.root }}
      title={<Text fw="400">Добавить документы</Text>}
      opened={uploadDocumentPopup.isOpen}
      onClose={handleClose}
      closeOnClickOutside={!meta.isLoading}
      closeOnEscape={!meta.isLoading}
    >
      <DrawerLayout
        actions={
          <Flex align="flex-end">
            <Button
              onClick={handleAddDocument}
              disabled={
                files.length === 0 || uploadDocumentStore.hasError || !documentSourceTypeSelectStore.serverFormat
              }
              loading={meta.isLoading}
              size="sm"
              className={s.root__button}
            >
              {addButtonLabel}
            </Button>
          </Flex>
        }
      >
        <div className={s.root__content}>
          <FileInput
            multiple
            size="sm"
            accept={ACCEPTED_FILE_TYPES}
            leftSection={<IconFileDescription size={16} className={s.root__icon} />}
            className={s.root__upload}
            placeholder="Выбрать файлы"
            value={files}
            onChange={handleChange}
            disabled={meta.isLoading}
            error={uploadDocumentStore.hasError}
            description={`Не более ${MAX_UPLOAD_FILES} файлов, до ${MAX_UPLOAD_FILE_SIZE_MB} MB каждый`}
            label={
              <Text className={s.root__required} size="sm">
                Загрузите файлы
              </Text>
            }
            clearable
          />
          <SourceSelect store={documentSourceTypeSelectStore} disabled={meta.isLoading} className={s.root__upload} />
          <UploadProgress store={uploadDocumentStore} />
          {showFailedFilesAlert && (
            <Alert className={s.root__alert} color="red" title={failedFilesAlertTitle}>
              <List size="sm" spacing="xs">
                {failedFiles.map(({ file, message }) => (
                  <List.Item key={`${file.name}-${file.size}-${file.lastModified}`} className={s['root__alert-item']}>
                    {file.name}: {message}
                  </List.Item>
                ))}
              </List>
            </Alert>
          )}
          <Text size="sm">Добавить в:</Text>
          {folder ? (
            <TreeNode level={1} isOpen={false} withArrow={false} icon={<IconFolder />} title={folder.name} />
          ) : (
            <TreeSelect />
          )}
        </div>
      </DrawerLayout>
    </Drawer>
  );
};

export default observer(UploadDocumentPopup);
